import { FIRST_PURCHASE_UPLOADED_USERS_KEY } from "./FirstPurchaseAttribution";

export const RECHARGE_ORDERS_KEY = "bg_waitting_recharge_orders";
export const RECHARGE_SNAPSHOTS_KEY = "recharge_attribution_order_snapshots";

const text = (value) => value == null ? "" : String(value).trim();
const object = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
const orders = (value) => Array.isArray(value) ? value : [];
const positive = (value) => Number.isFinite(Number(value)) && Number(value) > 0;
const delivered = (event) => ["sdk_queued", "native_dispatched_unconfirmed", "not_applicable", "not_required"].includes(event && event.status);
const queuedFirst = (event) => ["sdk_queued", "native_dispatched_unconfirmed"].includes(event && event.status);
const flag = (value) => {
  if ([true, 1, "1", "true"].includes(value)) return true;
  if ([false, 0, "0", "false"].includes(value)) return false;
  return null;
};
const firstFlag = (input) => {
  const value = object(input);
  return flag(value.is_first_purchase ?? value.isFirstPurchase ?? value.first_purchase ?? value.is_first_recharge);
};

const sanitizeEvent = (event) => {
  const value = object(event);
  return { ...(value.status ? { status: text(value.status) } : {}), ...(value.reason ? { reason: text(value.reason) } : {}) };
};

// Persist only attribution metadata, never the authenticated account or payment URL.
const sanitize = (input) => {
  const value = object(input);
  const result = text(value.order_id) ? { order_id: text(value.order_id) } : {};
  ["owner_rid", "rid", "currency", "first_purchase_source", "last_reason"].forEach((key) => {
    if (text(value[key])) result[key] = text(value[key]);
  });
  ["value", "created_at", "payment_confirmed_at", "attempts", "next_attempt_at"].forEach((key) => {
    if (value[key] != null && Number.isFinite(Number(value[key]))) result[key] = Number(value[key]);
  });
  ["payment_confirmed", "completed", "requires_review"].forEach((key) => {
    if (typeof value[key] === "boolean") result[key] = value[key];
  });
  if (Object.prototype.hasOwnProperty.call(value, "is_first_purchase")) result.is_first_purchase = firstFlag(value);
  if (value.delivery) {
    result.delivery = {
      purchase: sanitizeEvent(value.delivery.purchase),
      firstPurchase: sanitizeEvent(value.delivery.firstPurchase),
      otherAnalyticsDispatched: value.delivery.otherAnalyticsDispatched === true,
    };
  }
  return result;
};

const merge = (previous, incoming) => {
  const result = { ...sanitize(previous), ...sanitize(incoming) };
  if (firstFlag(incoming) === null && firstFlag(previous) !== null) result.is_first_purchase = firstFlag(previous);
  if ((previous && previous.delivery) || (incoming && incoming.delivery)) {
    const before = object(previous && previous.delivery);
    const after = object(incoming && incoming.delivery);
    const rank = (event) => ({ sdk_queued: 4, native_dispatched_unconfirmed: 3, not_applicable: 2, not_required: 1 }[event && event.status] || 0);
    const stronger = (one, two) => rank(one) > rank(two) ? sanitizeEvent(one) : sanitizeEvent(two || one);
    result.delivery = {
      purchase: stronger(before.purchase, after.purchase), firstPurchase: stronger(before.firstPurchase, after.firstPurchase),
      otherAnalyticsDispatched: before.otherAnalyticsDispatched === true || after.otherAnalyticsDispatched === true,
    };
  }
  if (previous && previous.payment_confirmed) result.payment_confirmed = true;
  if (previous && previous.completed) result.completed = true;
  return result;
};

export const rememberRechargeOrder = (store, input) => {
  const incoming = sanitize(input);
  if (!incoming.order_id) return null;
  let saved;
  store.update(RECHARGE_SNAPSHOTS_KEY, (current) => {
    const snapshots = object(current);
    saved = merge(snapshots[incoming.order_id], incoming);
    if (saved.is_first_purchase == null) saved.is_first_purchase = null;
    if (!positive(saved.created_at)) saved.created_at = Date.now();
    return { ...snapshots, [saved.order_id]: saved };
  });
  store.update(RECHARGE_ORDERS_KEY, (current) => {
    const queue = orders(current);
    if (saved.completed) return queue.filter((item) => text(item.order_id) !== saved.order_id);
    const existing = queue.find((item) => text(item.order_id) === saved.order_id);
    const next = merge(existing, saved);
    return [...queue.filter((item) => text(item.order_id) !== saved.order_id), next];
  });
  return saved;
};

export const restoreRechargeOrder = (store, orderId, userInfo, currency) => {
  const id = text(orderId);
  if (!id) return null;
  const snapshot = object(store.read(RECHARGE_SNAPSHOTS_KEY))[id];
  const queued = orders(store.read(RECHARGE_ORDERS_KEY)).find((item) => text(item.order_id) === id);
  const existing = merge(queued, snapshot);
  const saved = rememberRechargeOrder(store, {
    ...existing,
    order_id: id,
    currency: existing.currency || currency,
    is_first_purchase: firstFlag(existing),
    first_purchase_source: existing.first_purchase_source || "unknown",
  });
  return saved;
};

export const createRechargeAttribution = ({ read, update, sendCommand, sendAttribution, currency, now = Date.now }) => {
  const inFlight = new Set();
  const userLocks = new Map();
  const withUserLock = async (rid, work) => {
    const previous = userLocks.get(rid) || Promise.resolve();
    let release;
    const gate = new Promise((resolve) => { release = resolve; });
    const current = previous.then(() => gate);
    userLocks.set(rid, current);
    await previous;
    try { return await work(); } finally {
      release();
      if (userLocks.get(rid) === current) userLocks.delete(rid);
    }
  };
  const markFirstHandoff = (id, rid, firstPurchase) => {
    if (!queuedFirst(firstPurchase)) return;
    update(FIRST_PURCHASE_UPLOADED_USERS_KEY, (current) => ({
      ...object(current),
      [`rid:${rid}`]: object(current)[`rid:${rid}`] || {
        order_id: id, uploaded_at: now(),
        source: firstPurchase.status === "sdk_queued" ? "recharge" : "recharge_native_handoff",
      },
    }));
  };
  const currentRecord = (id) => {
    const queued = orders(read(RECHARGE_ORDERS_KEY)).find((item) => text(item.order_id) === id);
    return merge(queued, object(read(RECHARGE_SNAPSHOTS_KEY))[id]);
  };
  const patch = (id, changes) => {
    update(RECHARGE_SNAPSHOTS_KEY, (current) => ({
      ...object(current), [id]: sanitize({ ...currentRecord(id), ...changes, order_id: id }),
    }));
    update(RECHARGE_ORDERS_KEY, (current) => orders(current).map((item) =>
      text(item.order_id) === id ? sanitize({ ...item, ...changes }) : item));
    return currentRecord(id);
  };
  const finish = (id) => {
    patch(id, { completed: true, next_attempt_at: 0 });
    update(RECHARGE_ORDERS_KEY, (current) => orders(current).filter((item) => text(item.order_id) !== id));
  };
  const defer = (id, reason, delay) => {
    const record = currentRecord(id);
    const attempts = (Number(record.attempts) || 0) + 1;
    const age = now() - (Number(record.created_at) || now());
    const pendingWait = age >= 7 * 86400000 ? 6 * 3600000 : age >= 86400000 ? 3600000 : age >= 15 * 60000 ? 60000 : 10000;
    const retryWait = Math.min(300000, Math.max(age > 15 * 60000 ? 60000 : 10000, 10000 * Math.pow(2, Math.min(attempts - 1, 5))));
    const wait = delay || (reason === "payment_not_confirmed" ? pendingWait : retryWait);
    patch(id, { attempts, last_reason: reason, next_attempt_at: now() + wait });
  };

  const process = async (id, historyRequests) => {
    if (inFlight.has(id)) return;
    inFlight.add(id);
    try {
      let record = currentRecord(id);
      if (record.completed) { finish(id); return; }
      if (record.requires_review || Number(record.next_attempt_at) > now()) return;
      const user = read("user_info");
      const rid = text(user && user.rid);
      if (!rid || !user.token) return;
      const sameAccount = () => {
        const current = read("user_info");
        return Boolean(current && text(current.rid) === rid && current.token === user.token);
      };
      const owner = text(record.owner_rid || record.rid);
      if (owner && owner !== rid) {
        return;
      }

      const previousPurchase = object(object(record.delivery).purchase);
      const needsFirstFlag = firstFlag(record) === null && delivered(previousPurchase) && previousPurchase.status !== "not_applicable";
      if (!owner || !positive(record.value) || needsFirstFlag) {
        let matched;
        let totalPages = 1;
        for (let page = 1; page <= totalPages && page <= 10; page++) {
          if (!sameAccount()) return;
          const key = `${rid}:${user.token}:${page}`;
          if (!historyRequests.has(key)) historyRequests.set(key, Promise.resolve(sendCommand("payOrders", {
            token: user.token, order_type: "deposit", range_type: "30days", page,
          })));
          const response = await historyRequests.get(key);
          if (!sameAccount()) return;
          if (!response || response.status === false || !Array.isArray(response.rows)) break;
          matched = response.rows.find((item) => text(item.order_id) === id);
          if (matched) break;
          const pages = Number(response.total_page);
          totalPages = Number.isFinite(pages) && pages > 0 ? Math.floor(pages) : 1;
        }
        if (matched) {
          const fields = { owner_rid: rid, rid, currency: record.currency || currency };
          if (!positive(record.value) && positive(matched.price)) fields.value = Number(matched.price) / 100;
          if (firstFlag(record) === null && firstFlag(matched) !== null) {
            fields.is_first_purchase = firstFlag(matched);
            fields.first_purchase_source = "payOrders";
          }
          record = patch(id, fields);
        } else if (!owner) {
          defer(id, "ownership_unverified", 300000);
          return;
        }
      }
      if (!sameAccount()) return;
      if (!positive(record.value)) { defer(id, "missing_revenue", 300000); return; }
      const eventCurrency = text(record.currency || currency).toUpperCase();
      if (!/^[A-Z]{3}$/.test(eventCurrency)) { defer(id, "missing_currency", 300000); return; }

      if (!record.payment_confirmed) {
        const response = await sendCommand("checkRechargeOrder", { token: user.token, order_id: id });
        if (!sameAccount()) return;
        if (!response || !response.status || typeof response.data !== "string" || !text(response.data)) {
          defer(id, "order_query_failed");
          return;
        }
        if (!["SUCCESS", "MANUAL"].includes(response.data)) {
          defer(id, "payment_not_confirmed");
          return;
        }
        record = patch(id, { payment_confirmed: true, payment_confirmed_at: now() });
      }

      await withUserLock(rid, async () => {
        if (!sameAccount()) return;
        record = currentRecord(id);
        const first = firstFlag(record);
        const uploaded = object(read(FIRST_PURCHASE_UPLOADED_USERS_KEY))[`rid:${rid}`] ||
          Object.values(object(read(RECHARGE_SNAPSHOTS_KEY))).some((item) =>
            text(item.owner_rid || item.rid) === rid && queuedFirst(object(item.delivery).firstPurchase));
        const ledger = object(record.delivery);
        let purchase = object(ledger.purchase);
        let firstPurchase = object(ledger.firstPurchase);
        if (first === false || (first === true && uploaded && !delivered(firstPurchase))) firstPurchase = { status: "not_required" };
        const purchaseQueued = delivered(purchase);
        const firstPurchaseQueued = first !== true || delivered(firstPurchase);
        if (purchaseQueued && (first === false || delivered(firstPurchase)) &&
            (purchase.status !== "not_applicable" || ledger.otherAnalyticsDispatched === true)) {
          if (first === true) markFirstHandoff(id, rid, firstPurchase);
          finish(id);
          return;
        }
        if (purchaseQueued && first === null && purchase.status !== "not_applicable") { defer(id, "first_purchase_unknown", 300000); return; }
        if (!sameAccount()) return;

        const data = {
          items: [{ productId: "1", price: "1", quantity: "#1" }],
          transactionId: id, rid: user.rid, value: Number(record.value), revenue: Number(record.value),
          currency: eventCurrency, isFirstPurchase: first === true && !delivered(firstPurchase),
          is_first_purchase: first === true && !delivered(firstPurchase),
        };
      const result = await sendAttribution(data, {
        purchaseQueued, firstPurchaseQueued, skipOtherAnalytics: ledger.otherAnalyticsDispatched === true,
        canDispatch: sameAccount,
      });
      if (!sameAccount()) return;
      if (result && result.purchase && !purchaseQueued) purchase = sanitizeEvent(result.purchase);
      if (result && result.firstPurchase && !delivered(firstPurchase) && (first === true || result.firstPurchase.status === "not_applicable")) firstPurchase = sanitizeEvent(result.firstPurchase);
      if (result && result.transport === "none" && purchase.status === "not_applicable" &&
          (ledger.otherAnalyticsDispatched === true || result.otherAnalyticsDispatched === true)) firstPurchase = { status: "not_applicable" };
      const delivery = {
        purchase, firstPurchase,
        otherAnalyticsDispatched: ledger.otherAnalyticsDispatched === true || Boolean(result && result.otherAnalyticsDispatched),
      };
      const requiresReview = [purchase, firstPurchase].some((event) =>
        ["duplicate_unverified", "sdk_duplicate_unverified"].includes(event.reason) ||
        ["duplicate_unverified", "sdk_duplicate_unverified"].includes(event.status));
      record = patch(id, { delivery, requires_review: requiresReview });
      if (first === true) markFirstHandoff(id, rid, firstPurchase);
      if (requiresReview) return;
      const done = delivered(purchase) && (delivered(firstPurchase) || first === false) &&
        (purchase.status !== "not_applicable" || delivery.otherAnalyticsDispatched);
      if (done) finish(id);
      else defer(id, first === null ? "first_purchase_unknown" : "attribution_retry", first === null ? 300000 : undefined);
      });
    } catch (error) {
      try { defer(id, "request_or_storage_failed"); } catch (storageError) {
        // Preserve the existing snapshot for recovery when storage becomes available.
      }
    } finally {
      inFlight.delete(id);
    }
  };

  return {
    run: async () => {
      update(RECHARGE_ORDERS_KEY, (current) => {
        const queue = orders(current);
        const knownIds = new Set(queue.filter(Boolean).map((item) => text(item.order_id)));
        const recovered = [];
        Object.values(object(read(RECHARGE_SNAPSHOTS_KEY))).forEach((snapshot) => {
          const record = sanitize(snapshot);
          if (record.order_id && !record.completed && !knownIds.has(record.order_id)) {
            recovered.push(record);
            knownIds.add(record.order_id);
          }
        });
        return recovered.length ? [...queue, ...recovered] : queue;
      });
      const historyRequests = new Map();
      return Promise.all(orders(read(RECHARGE_ORDERS_KEY)).filter((item) => item && text(item.order_id)).map((item) => process(text(item.order_id), historyRequests)));
    },
  };
};
